/*
 * RetechVN Pro – core utilities.
 * Shared helpers used by Liquid templates and the other theme scripts.
 * Must be loaded (deferred) before every other theme script.
 */

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* ---------- Pub/sub ---------- */

const subscribers = {};

function subscribe(eventName, callback) {
  if (!subscribers[eventName]) subscribers[eventName] = [];
  subscribers[eventName].push(callback);
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
  };
}

function publish(eventName, data) {
  (subscribers[eventName] || []).forEach((callback) => callback(data));
}

/* ---------- General helpers ---------- */

function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function getSectionInnerHTML(html, selector = '.shopify-section') {
  const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return element ? element.innerHTML : '';
}

const defaultDirection = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
const isRtl = defaultDirection === 'rtl';

/* ---------- Money ---------- */

function formatMoney(cents, format = window.moneyFormat || '${{amount}}') {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = Number(cents) || 0;

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    const fixed = (number / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const withThousands = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? withThousands + decimal + fraction : withThousands;
  };

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  if (!match) return format;

  let value;
  switch (match[1]) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ', '');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }
  return format.replace(placeholder, value);
}

/* ---------- Focus management ---------- */

const trapFocusHandlers = {};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetails = event.target.closest('details[open]');
  if (!openDetails) return;
  const summary = openDetails.querySelector('summary');
  openDetails.removeAttribute('open');
  summary.setAttribute('aria-expanded', false);
  summary.focus();
}

/* ---------- Media ---------- */

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('media-gallery video, .product-media-modal video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/* ---------- Dates (estimated shipping / checkpoints) ---------- */

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/* ---------- Shopify helpers (customer address forms) ---------- */

window.Shopify = window.Shopify || {};

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0; i < selector.options.length; i++) {
    const option = selector.options[i];
    if (value === option.value || value === option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener(eventName, callback, false);
};

Shopify.postLink = function (path, options = {}) {
  const form = document.createElement('form');
  form.setAttribute('method', options.method || 'post');
  form.setAttribute('action', path);
  Object.entries(options.parameters || {}).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};

Shopify.CountryProvinceSelector = function (countryId, provinceId, options = {}) {
  this.countryEl = document.getElementById(countryId);
  this.provinceEl = document.getElementById(provinceId);
  this.provinceContainer = document.getElementById(options.hideElement || provinceId);
  if (!this.countryEl || !this.provinceEl) return;

  this.countryEl.addEventListener('change', this.countryHandler.bind(this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry() {
    Shopify.setSelectorByValue(this.countryEl, this.countryEl.getAttribute('data-default'));
    this.countryHandler();
  },

  initProvince() {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) Shopify.setSelectorByValue(this.provinceEl, value);
  },

  countryHandler() {
    const option = this.countryEl.options[this.countryEl.selectedIndex];
    const provinces = JSON.parse((option && option.getAttribute('data-provinces')) || '[]');

    this.provinceEl.innerHTML = '';
    if (provinces.length === 0) {
      this.provinceContainer.style.display = 'none';
      return;
    }
    provinces.forEach(([value, label]) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.innerHTML = label;
      this.provinceEl.appendChild(opt);
    });
    this.provinceContainer.style.display = '';
  },
};

/* ---------- Cart API ---------- */

window.theme = window.theme || {};

theme.cart = {
  async get() {
    const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  },

  /**
   * Re-render the cart drawer / cart page / cart bubble after an out-of-band change
   * (discount codes, gifts, upsells...). Implemented by cart.js; no-op until it loads.
   */
  refresh() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
  },
};

/* ---------- Cart discount field (snippets/cart-discount-field.liquid) ---------- */

function handleDiscountFormChange(event) {
  const form = event.target.closest('.cart-discount-form');
  if (form) form.classList.remove('cart-discount-form--error');
}

async function handleDiscountForm(event) {
  event.preventDefault();
  const form = event.target;
  const input = form.querySelector('[name="cart-discount-field"]');
  const button = form.querySelector('[type="submit"]');
  const code = input.value.trim();
  if (!code) return;

  button.classList.add('loading');
  button.setAttribute('disabled', '');

  try {
    const current = await theme.cart.get();
    const existing = (current.discount_codes || []).map((d) => d.code);
    const codes = [...new Set([...existing, code])].join(',');

    const response = await fetch(`${window.routes.cart_update_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: codes }),
    });
    const cart = await response.json();
    const applied = (cart.discount_codes || []).find(
      (d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable
    );

    if (applied) {
      input.value = '';
      theme.cart.refresh();
    } else {
      form.classList.add('cart-discount-form--error');
    }
  } catch (error) {
    console.error(error);
    form.classList.add('cart-discount-form--error');
  } finally {
    button.classList.remove('loading');
    button.removeAttribute('disabled');
  }
}

/* ---------- Quantity input ---------- */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    if (!this.input) return;
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.currentTarget;

    if (button.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    const min = parseInt(this.input.min);
    const max = parseInt(this.input.max);
    const minus = this.querySelector(".quantity__button[name='minus']");
    const plus = this.querySelector(".quantity__button[name='plus']");
    if (minus) minus.classList.toggle('disabled', !isNaN(min) && value <= min);
    if (plus) plus.classList.toggle('disabled', !isNaN(max) && value >= max);
  }
}

if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
